import type { ProductOption } from '@shared/components/ui/ProductCombobox';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

const fetchBatchActiveProducts = async (): Promise<ProductOption[]> => {
  const httpClient = createHttpClient(apiClientWH);
  const allProducts: ProductOption[] = [];
  let currentPage = 1;
  let hasMore = true;

  while (hasMore) {
    const response = await httpClient.get<any>('/products', {
      order_by: 'id',
      order_direction: 'desc',
      page: currentPage,
      per_page: 100,
      'advanced_filters[filters][0][column]': 'batch_active',
      'advanced_filters[filters][0][operator]': 'equals',
      'advanced_filters[filters][0][value][]': 'true',
      'advanced_filters[logic]': 'AND',
    });

    const products = response.data || [];
    allProducts.push(
      ...products.map((p: any) => ({
        id: p.id,
        name: p.name || `Producto ${p.id}`,
        code: p.code,
        image: p.image_hash,
        value: p.id.toString(),
        stock: p.stock,
      })),
    );

    const meta = response.meta;
    hasMore = meta ? meta.currentPage < meta.lastPage : false;
    currentPage++;

    if (currentPage > 100) break;
  }

  return allProducts;
};

const loadBatchActiveProductsPage = async (params: {
  search: string;
  page: number;
  limit: number;
}): Promise<LoadOptionsResponse> => {
  const httpClient = createHttpClient(apiClientWH);
  const queryParams: Record<string, unknown> = {
    order_by: 'id',
    order_direction: 'desc',
    page: params.page,
    per_page: params.limit,
    'advanced_filters[filters][0][column]': 'batch_active',
    'advanced_filters[filters][0][operator]': 'equals',
    'advanced_filters[filters][0][value][]': 'true',
    'advanced_filters[logic]': 'AND',
  };

  if (params.search.trim()) {
    queryParams.search = params.search.trim();
  }

  const response = await httpClient.get<any>('/products', queryParams, {}, false);
  const products = response.data || [];
  const meta = response.meta;

  return {
    options: products.map((p: { id: number; name?: string; code?: string }) => ({
      value: String(p.id),
      label: p.name || `Producto ${p.id}`,
      description: p.code || undefined,
    })),
    hasMore: meta ? meta.currentPage < meta.lastPage : false,
  };
};

export const useBatchProductSearch = () => {
  const { data: productOptions = [], isLoading } = useQuery<ProductOption[]>({
    queryKey: ['products', 'batch_active'],
    queryFn: fetchBatchActiveProducts,
    staleTime: 5 * 60 * 1000,
  });

  const searchProductsForCombobox = async (query: string): Promise<ProductOption[]> => {
    if (!query.trim()) return productOptions.slice(0, 100);

    const queryLower = query.toLowerCase();
    return productOptions
      .filter((p) => p.name.toLowerCase().includes(queryLower) || p.code?.toLowerCase().includes(queryLower))
      .slice(0, 200);
  };

  const loadProductsForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }) => loadBatchActiveProductsPage(params),
    [],
  );

  return { searchProductsForCombobox, loadProductsForMultiSelect, isLoading };
};
